import { useHeldOddChange } from 'hooks/useHeldOddChange';
import { useContext, useEffect, useState } from 'react';
import styles from './BetOdd.module.css';
import { Odd } from 'types/Odd';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { Bet } from 'types/Bet';
import { MatchOffer } from 'types/MatchOffer';
import { SlipContext } from 'context/slipContext';
import { LiveEvent, LiveEventBet, LiveEventOdd } from 'types/LiveEvent';
import { LiveBettingContext } from 'context/liveBettingContext';
import { CssLabels } from 'types/enums';
import { useTranslation } from 'react-i18next';
import { formatTranslatedOddLabelParts } from 'helpers/betI18n';
import {
  isSuspendedOdd,
  SuspendedLock,
} from 'components/SuspendedLock/SuspendedLock';

interface Props {
  odd: Odd | LiveEventOdd;
  bet: Bet | LiveEventBet;
  match: MatchOffer | LiveEvent;
  isLive?: boolean;
  /** Short name for the pick when its card already says the line ("Preko",
   *  "1X"); the slip still shows the full one. */
  label?: string;
}

export const BetOdd = ({ odd, bet, match, isLive = false, label }: Props) => {
  const { t } = useTranslation();
  const [prevData, setPrevData] = useState<any>(null);

  const { addSlipEntry, removeSlipEntry, entryExist } = useContext(SlipContext);
  const { events, getLastOddsForEvent } = useContext(LiveBettingContext);

  const { isMobile } = useBreakpoint();

  // Read off the slip on every render: a pick drawn again (a row moved or
  // replaced by a live update) started unselected until the slip changed.
  const selected = entryExist(match, bet, odd);

  useEffect(() => {
    if (isLive) {
      const data = getLastOddsForEvent(match);
      if (data) {
        setPrevData(data);
      }
    }
  }, [events]);

  // Suspended by bet365 for the moment: a lock, not a price to pick.
  const suspended = isLive && isSuspendedOdd(odd);
  const handleOddClick = () => {
    if (suspended && !selected) return;
    if (selected) {
      removeSlipEntry(match, bet, odd);
    } else {
      addSlipEntry(match, bet, odd, isLive);
    }
  };

  const getDataChangedCssValue = (
    betName: string,
    oddId: string
  ): CssLabels => {
    let cssLabel: CssLabels = CssLabels.SameValue;
    if (prevData) {
      const prevBet = prevData.bets.find(
        (x: LiveEventBet) => x.name === betName
      );
      if (prevBet && bet) {
        const prevOdd = prevBet.odds.find(
          (x: LiveEventBet) => x.custom_id === oddId
        );

        if (prevOdd && odd) {
          if (prevOdd.odds > odd.odds) {
            cssLabel = CssLabels.LowerValue;
          } else if (prevOdd.odds < odd.odds) {
            cssLabel = CssLabels.HigherValue;
          }
        }
      }
    }

    return cssLabel;
  };

  const {
    name: oddName,
    handicap: oddHandicap,
    header: oddHeader,
    extra: oddExtra,
  } = formatTranslatedOddLabelParts(odd, t);
  const mark = useHeldOddChange(
    isLive ? getDataChangedCssValue(bet.name, odd.custom_id + '') : '',
    odd.odds
  );

  return (
    // The whole pick is the button - its name and its price - on desktop as
    // on phones (betbigwin style), not just the price box.
    <div
      className={styles.odd}
      data-selected={selected}
      key={mark.key}
      data-changed={suspended ? '' : mark.label}
      data-suspended={suspended || undefined}
      onClick={handleOddClick}
    >
      <div className={styles.oddTitle}>
        {label ? (
          <span>{label}</span>
        ) : (
          <>
            {oddName && <span>{oddName}</span>}
            {oddHandicap && <span> {oddHandicap}</span>}
            {oddHeader && <span> ({oddHeader})</span>}
            {oddExtra && <span> ({oddExtra})</span>}
          </>
        )}
      </div>
      <div
        className={styles.oddValue}
        data-selected={!isMobile && selected}
        data-changed={suspended ? '' : mark.label}
      >
        {suspended ? <SuspendedLock /> : odd.odds.toFixed(2)}
      </div>
    </div>
  );
};
